import { Box, Grid } from '@mui/material'
import FormInput from 'components/FormInput'
import InputDatePicker from 'components/FormInputDatePicker'
import { FormInputSelect, ItemOption } from 'components/FormInputSelect'
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { HearingIdentificationType } from 'types/hearing-identification.type'
import UploadPicture from 'components/UploadPicture'
import ModalSearchTeenager from 'pages/CreateHearing/components/StepIdentification/components/ModalSearchTeenager'
import { useHearingIdentificationBlockFieldsContext } from 'contexts/hearing-identification-block-fields.context'
import { base64ToFile, obterTamanhoArquivo } from 'utils/base64.utils'
import dayjs from 'dayjs'
import { isFilled } from 'utils/validate.util'
import { useSnackBar } from 'contexts/snackbar.context'

type PersonalDataGroupProps = {
  genderOptions: ItemOption[]
  skinOptions: ItemOption[]
}

const PersonalDataGroup: React.FC<PersonalDataGroupProps> = ({ genderOptions, skinOptions }) => {
  const { showSnackBar } = useSnackBar()
  const { control, setValue, reset, watch } = useFormContext<HearingIdentificationType>()
  const { isBlockedFields, handleUnblockAllFields } = useHearingIdentificationBlockFieldsContext()

  const [disabledDataNascimento, setDisabledDataNascimento] = useState<boolean>(true)
  const [disabledNome, setDisabledNome] = useState<boolean>(true)

  const handleChangeAdolescente = async (value?: HearingIdentificationType) => {
    setDisabledDataNascimento(false)
    setDisabledNome(false)
    handleUnblockAllFields()
    if (value) {
      reset(value, {
        keepDefaultValues: true,
      })
    }
  }
  const foto = watch('foto')
  const dataNascimentoWatch = watch('dataNascimento')

  const imagePicture = useMemo(
    () => base64ToFile(foto?.arquivoBase64, foto?.arquivoNome, foto?.arquivoTipo),
    [foto?.arquivoBase64, foto?.arquivoNome, foto?.arquivoTipo],
  )

  const handleChangePicture = async (e: ChangeEvent<HTMLInputElement>) => {
    const allowedExtensions = ['image/png', 'image/bmp', 'image/tiff', 'image/jpeg', 'image/jpg']
    const maxSizeInMB = 10
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024

    const file = e.target.files?.[0]
    if (file) {
      if (!allowedExtensions.includes(file.type)) {
        showSnackBar('Apenas arquivos .png, .bmp, .tiff, .jpg, .jpeg são permitidos.', 'warning')
        return
      }

      if (file.size > maxSizeInBytes) {
        showSnackBar(`O arquivo excede o tamanho máximo permitido de ${maxSizeInMB} MB.`, 'error')
        return
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        let base64Image = reader.result as string

        if (base64Image.includes(',')) {
          base64Image = base64Image.split(',')[1]
        }

        setValue('foto.id', foto?.id || null, { shouldDirty: true })
        setValue('foto.arquivoBase64', base64Image, { shouldDirty: true })
        setValue('foto.arquivoNome', file.name, { shouldDirty: true })
        setValue('foto.arquivoTipo', file.type, { shouldDirty: true })
      }
      reader.readAsDataURL(file) // Inicia a leitura do arquivo
    }
  }

  const handleResetPicture = async () => {
    setValue('foto', null, { shouldDirty: true })
  }

  useEffect(() => {
    if (isFilled(dataNascimentoWatch)) {
      const today = dayjs()
      const birth = dayjs(dataNascimentoWatch)
      const age = today.diff(birth, 'year') // Calcula a idade em anos
      setValue('idade', age.toString()) // Atualiza o campo de idade no formulário
    }
  }, [dataNascimentoWatch])

  return (
    <>
      <Grid item lg={3} md={4} xs={12}>
        <UploadPicture
          image={imagePicture}
          handleChange={handleChangePicture}
          handleReset={handleResetPicture}
          disabled={isBlockedFields}
          metadados={{
            nomeArquivo: foto?.arquivoNome,
            tipoArquivo: foto?.arquivoTipo,
            tamanho: obterTamanhoArquivo(foto?.arquivoBase64),
          }}
        />
      </Grid>
      <Grid item lg={9} md={8} xs={12}>
        <Grid container spacing={2} rowSpacing={4}>
          <Grid item xs={12}>
            <FormInput
              control={control}
              name='nomeSocial'
              label='Nome social'
              placeholder='Informe o nome social'
              maxLength={60}
              disabled={isBlockedFields}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display='flex' flexDirection='row' alignContent='center'>
              <FormInput
                control={control}
                name='nome'
                label='Nome*'
                placeholder='Informe o nome'
                maxLength={60}
                disabled={disabledNome && isBlockedFields}
              />
              <ModalSearchTeenager onChangeAdolescente={handleChangeAdolescente} />
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <InputDatePicker
              control={control}
              name='dataNascimento'
              label='Data de nascimento'
              disabled={disabledDataNascimento && isBlockedFields && isBlockedFields}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInput
              control={control}
              name='idade'
              label='Idade'
              placeholder='Informe a idade'
              disabled
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInputSelect
              control={control}
              name='idGenero'
              label='Gênero'
              options={genderOptions}
              disabled={isBlockedFields}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <FormInputSelect
              control={control}
              name='idRaca'
              label='Cor'
              options={skinOptions}
              disabled={isBlockedFields}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default PersonalDataGroup
